import { useId, useRef, useState } from 'react';
import type { ComponentProps, MouseEvent, ReactNode } from 'react';

import { SheetHandle } from '@playbook/atoms/sheet-handle';
import { Menu } from '@playbook/compositions/menu';
import type { MenuSize } from '@playbook/compositions/menu';
import { useMediaQuery } from '@playbook/modules/layout/media-query';
import { cn, cv } from '@playbook/utils';
import { useAnchorPosition } from '@playbook/utils/anchor';
import { usePopoverClickGuard } from '@playbook/utils/popover-guard';
import { useSwipeToDismiss } from '@playbook/utils/swipe';

import css from './styles.module.css';

export type ContextMenuAppearFrom =
	| 'bottomLeft'
	| 'bottomRight'
	| 'topLeft'
	| 'topRight';

const DefaultContextMenuAppearFrom: ContextMenuAppearFrom =
	'bottomLeft' as const;

export type ContextMenuVariant = 'auto' | 'menu' | 'sheet';

const DefaultContextMenuVariant: ContextMenuVariant = 'auto' as const;

const DefaultContextMenuSize: MenuSize = 'md' as const;

const DefaultContextMenuCloseOnSelect: boolean = true;

const DefaultContextMenuInheritTriggerWidth: boolean = false;

const ContextMenuSheetQuery: string = '(max-width: 580px)';

type ContextMenuEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface ContextMenuDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface ContextMenuProperties
	extends ContextMenuEventHandlers, ContextMenuDataAttributes {
	appearFrom?: ContextMenuAppearFrom;
	variant?: ContextMenuVariant;
	size?: MenuSize;
	closeOnSelect?: boolean;
	inheritTriggerWidth?: boolean;

	label: string;
	children: ReactNode;

	renderTriggerWith: (popoverTarget: string, open: boolean) => ReactNode;
}

export function ContextMenu({
	appearFrom = DefaultContextMenuAppearFrom,
	variant = DefaultContextMenuVariant,
	size = DefaultContextMenuSize,
	closeOnSelect = DefaultContextMenuCloseOnSelect,
	inheritTriggerWidth = DefaultContextMenuInheritTriggerWidth,

	label,
	children,

	renderTriggerWith,

	onClick,
	onToggle,

	...props
}: ContextMenuProperties): ReactNode {
	const instance: string = useId().replaceAll(':', '');
	const popover: string = `${instance}-menu`;

	const trigger = useRef<HTMLSpanElement>(null);
	const element = useRef<HTMLDivElement>(null);

	const [open, setOpen] = useState<boolean>(false);
	const narrow: boolean = useMediaQuery(ContextMenuSheetQuery);
	const sheet: boolean = variant === 'sheet' || (variant === 'auto' && narrow);

	useSwipeToDismiss(element, sheet, () => element.current?.hidePopover());
	usePopoverClickGuard(element, sheet);
	useAnchorPosition(trigger, element, !sheet, {
		placement: appearFrom,
		inheritWidth: inheritTriggerWidth,
	});

	const anchor = cv({ anchor: `--${instance}` });

	function handleClick(event: MouseEvent<HTMLDivElement>): void {
		onClick?.(event);

		if (!closeOnSelect || !(event.target instanceof Element)) {
			return;
		}

		const item: Element | null = event.target.closest('button:not(:disabled)');

		if (item && event.currentTarget.contains(item)) {
			event.currentTarget.hidePopover();
		}
	}

	return (
		<span
			ref={trigger}
			className={cn(css.anchor, inheritTriggerWidth && css.inheritTriggerWidth)}
			style={anchor}>
			{renderTriggerWith(popover, open)}

			<div
				{...props}
				ref={element}
				id={popover}
				popover="auto"
				style={anchor}
				data-appear-from={appearFrom}
				data-sheet={sheet}
				className={cn(
					css.popover,
					inheritTriggerWidth && css.inheritTriggerWidth
				)}
				onClick={handleClick}
				onToggle={(event) => {
					onToggle?.(event);
					setOpen(event.newState === 'open');
				}}>
				<Menu variant={sheet ? 'sheet' : 'menu'} size={size} label={label}>
					{children}
				</Menu>
				{sheet && <SheetHandle />}
			</div>
		</span>
	);
}
